import { http, HttpResponse } from 'msw';

const sessionStorageKey = 'mockClients';

const getCssVariable = (variableName: string) => {
  const rootStyles = getComputedStyle(document.documentElement);
  return rootStyles.getPropertyValue(`--${variableName}`).trim();
};

const initializeClients = () => {
  if (!sessionStorage.getItem(sessionStorageKey)) {
    const initialClients = [
      {
        id: '1',
        name: 'John Doe',
        email: 'mail@example.com',
        phone: '+44 020 7654 3210',
        additionalInfo: 'Lorem ipsum...',
      },
      {
        id: '2',
        name: 'Jane Smith',
        email: 'mail@example.com',
        phone: '+44 020 7654 3210',
        additionalInfo: 'Lorem ipsum...',
      },
    ];
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(initialClients));
  }
};

initializeClients();

if (!sessionStorage.getItem('bookings')) {
  sessionStorage.setItem(
    'bookings',
    JSON.stringify([
      {
        id: '42',
        name: 'Team Meeting',
        startDate: '2024-12-09T10:00:00',
        endDate: '2024-12-09T13:00:00',
        staff: 'Jenny Brown',
        staffId: 2,
        client: 'John Doe',
        notes: 'Weekly team sync',
        categoryColor: getCssVariable('epos-red') || 'red',
        originalColor: getCssVariable('epos-red') || 'red',
        paymentStatus: 'Pending',
        services: [
          { id: 99, name: 'Weekly meeting', categoryColor: getCssVariable('epos-red') || 'red' },
        ],
      },
      {
        id: '43',
        name: 'Client Presentation',
        startDate: '2024-12-09T14:00:00',
        endDate: '2024-12-09T16:30:00',
        staff: 'John Adams',
        staffId: 3,
        client: 'John Doe',
        notes: 'Q1 results',
        categoryColor: getCssVariable('epos-green') || 'green',
        originalColor: getCssVariable('epos-green') || 'green',
        paymentStatus: 'Pending',
        services: [
          {
            id: 100,
            name: 'Monthly meeting',
            categoryColor: getCssVariable('epos-green') || 'green',
          },
        ],
      },
    ])
  );
}

if (!sessionStorage.getItem('resources')) {
  sessionStorage.setItem(
    'resources',
    JSON.stringify([
      {
        id: 1,
        name: 'Don Taylor',
        title: 'Resident',
        eventColor: getCssVariable('epos-orange') || 'orange',
        image: 'dan.jpg',
      },
      {
        id: 2,
        name: 'John Adams',
        title: 'Fellow',
        eventColor: getCssVariable('epos-blue') || 'blue',
        image: 'malik.jpg',
      },
      {
        id: 3,
        name: 'Jenny Brown',
        title: 'Attending',
        eventColor: getCssVariable('epos-brown') || 'brown',
        image: 'linda.jpg',
      },
      {
        id: 4,
        name: 'Tim Anderson',
        title: 'Attending',
        eventColor: getCssVariable('epos-red') || 'red',
        image: 'angelo.jpg',
      },
    ])
  );
}

const calendarHandlers = [
  http.get('*/v1/booking/clients', () => {
    const clients = JSON.parse(sessionStorage.getItem(sessionStorageKey) || '[]');
    return HttpResponse.json({
      data: clients,
      metadata: {
        total: clients.length,
      },
    });
  }),

  http.post('*/v1/booking/clients', async ({ request }) => {
    const clients = JSON.parse(sessionStorage.getItem(sessionStorageKey) || '[]');
    const newClient = (await request.json()) as {
      name?: string;
      email?: string;
      phone?: string;
    };

    if (!newClient.name || !newClient.email || !newClient.phone) {
      return HttpResponse.json(
        {
          error: 'Name, email, and phone are required to add a client.',
        },
        { status: 422 }
      );
    }

    const newClientWithId = {
      id: String(Date.now()),
      ...newClient,
    };

    const updatedClients = [...clients, newClientWithId];
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(updatedClients));

    return HttpResponse.json(newClientWithId, { status: 201 });
  }),

  http.get('*/v1/booking/categories', () =>
    HttpResponse.json({
      rows: [
        {
          id: 1,
          name: 'Category name 1',
          color: getCssVariable('epos-purple') || 'purple',
          originalColor: getCssVariable('epos-purple') || 'purple',
          services: [
            {
              id: 101,
              name: 'Service name 1',
              duration: 120,
              price: 10.0,
              startTime: '12:00',
              endTime: '14:00',
              categoryColor: getCssVariable('epos-purple') || 'purple',
            },
            {
              id: 102,
              name: 'Service name 2',
              duration: 60,
              price: 15.0,
              startTime: '12:00',
              endTime: '13:00',
              categoryColor: getCssVariable('epos-purple') || 'purple',
            },
          ],
        },
        {
          id: 2,
          name: 'Category name 2',
          color: getCssVariable('epos-red') || 'red',
          originalColor: getCssVariable('epos-red') || 'red',
          services: [
            {
              id: 201,
              name: 'Service name 3',
              duration: 90,
              price: 20.0,
              startTime: '13:00',
              endTime: '14:30',
              categoryColor: getCssVariable('epos-red') || 'red',
            },
          ],
        },
        {
          id: 3,
          name: 'Category name 3',
          color: getCssVariable('epos-green') || 'green',
          originalColor: getCssVariable('epos-green') || 'green',
          services: [
            {
              id: 301,
              name: 'Service name 4',
              duration: 30,
              price: 5.0,
              startTime: '12:30',
              endTime: '13:00',
              categoryColor: getCssVariable('epos-green') || 'green',
            },
          ],
        },
      ],
    })
  ),

  http.get('*/v1/booking/resources', () => {
    const resources = JSON.parse(sessionStorage.getItem('resources') || '[]');
    return HttpResponse.json({ rows: resources });
  }),

  http.get('*/v1/booking/bookings', () => {
    const bookings = JSON.parse(sessionStorage.getItem('bookings') || '[]');
    return HttpResponse.json({ data: bookings });
  }),

  http.post('*/v1/booking/bookings', async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown> & { id?: string };
    const bookings = JSON.parse(sessionStorage.getItem('bookings') || '[]') as Record<
      string,
      unknown
    >[];

    if (body.id) {
      const idx = bookings.findIndex((b) => String(b.id) === String(body.id));
      const updated = { ...body, id: String(body.id) };
      if (idx >= 0) {
        bookings[idx] = updated;
      } else {
        bookings.push(updated);
      }
      sessionStorage.setItem('bookings', JSON.stringify(bookings));
      return HttpResponse.json(updated, { status: 200 });
    }

    const bookingWithId = {
      id: String(Date.now()),
      ...body,
    };
    bookings.push(bookingWithId);
    sessionStorage.setItem('bookings', JSON.stringify(bookings));
    return HttpResponse.json(bookingWithId, { status: 201 });
  }),
];

export default calendarHandlers;
